import { z } from 'zod';

import {
  isoDateTimeSchema,
  nonNegativeIntSchema,
  portSchema,
  uuidSchema,
} from '../common/primitives';

import { backupSnapshotSchema } from './snapshot';
import { storageBoxUsernameSchema } from './storage-box';

/**
 * Die Außensicht auf Storage Boxen und die Sicherung einer Site.
 *
 * Wie bei `site-view.ts` bis M7 eine Schnittstelle im Master, jetzt ein
 * Vertrag — aus demselben Grund und mit derselben Auswahl: **kein**
 * SSH-Schlüssel, **kein** Repository-Passwort. Beide verlassen die Datenbank
 * ausschließlich über das Manifest eines Nodes (ADR 0012), und ein Feld dafür
 * an dieser Stelle wäre der Anfang vom Ende dieser Zusage.
 */

/** Die Sicht des Betreibers auf eine Box — ohne Schlüssel. */
export const storageBoxViewSchema = z.object({
  id: uuidSchema,
  label: z.string().min(1).max(64),
  host: z.string().min(1).max(253),
  port: portSchema,
  username: storageBoxUsernameSchema,
  basePath: z.string(),
  capacityGb: nonNegativeIntSchema,
  /** Zuletzt eingetragene Belegung — ein Ist-Wert, von Hand gepflegt. */
  usedGb: nonNegativeIntSchema,
  /** Summe über `site_backup.reserved_gb`, gerechnet und nicht fortgeschrieben. */
  reservedGb: nonNegativeIntSchema,
  freeGb: nonNegativeIntSchema,
  siteCount: nonNegativeIntSchema,
  enabled: z.boolean(),
  /** Nimmt diese Box gerade neue Sites an — und wenn nicht, warum nicht? */
  acceptsNewSites: z.boolean(),
  rejectionMessage: z.string().nullable(),
});

export type StorageBoxView = z.infer<typeof storageBoxViewSchema>;

/** Die Sicht des Betreibers auf die Sicherung einer Site. */
export const siteBackupViewSchema = z.object({
  siteId: uuidSchema,
  /** `null`, solange keine Box das Repository dieser Site aufnehmen konnte. */
  storageBox: z
    .object({
      id: uuidSchema,
      label: z.string(),
    })
    .nullable(),
  /**
   * Die Repository-Adresse **ohne** Passwort.
   *
   * Sie steht hier, weil sie im Störfall die erste Frage beantwortet („wohin
   * schreibt diese Site eigentlich?"). Das Passwort steht nirgends in einer
   * Antwort — es gibt keinen Endpunkt, der es ausgibt.
   */
  repository: z.string(),
  reservedGb: nonNegativeIntSchema,
  initializedAt: isoDateTimeSchema.nullable(),
  lastSuccessAt: isoDateTimeSchema.nullable(),
  lastFailureAt: isoDateTimeSchema.nullable(),
  lastError: z.string().nullable(),
  nextRunAt: isoDateTimeSchema.nullable(),
  snapshots: z.array(backupSnapshotSchema),
});

export type SiteBackupView = z.infer<typeof siteBackupViewSchema>;
